import { useEffect, useRef, useState } from 'react'

export function formatDate(value: string | null, time = false) {
  if (!value) return 'Not observed'
  // Date-only legacy login fields must not shift to the preceding day in Chicago.
  const parsed = new Date(value.length === 10 ? `${value}T12:00:00Z` : value)
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric',
    ...(time ? { hour: 'numeric', minute: '2-digit' } as const : {}),
  }).format(parsed)
}

export function shortDay(day: string) {
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', weekday: 'short' })
    .format(new Date(`${day}T12:00:00Z`))
}

export function relativeTime(value: string, now = Date.now()) {
  const minutes = Math.round((now - new Date(value).getTime()) / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  return days < 30 ? `${days}d ago` : formatDate(value)
}

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/**
 * Eases from the previously shown value to the new one, so refreshes animate instead of jumping.
 * Rounds to `decimals` (money keeps its cents); the last frame is exactly `target`.
 */
export function useCountUp(target: number, duration = 1200, decimals = 0) {
  const [value, setValue] = useState(prefersReducedMotion() ? target : 0)
  const from = useRef(value)
  useEffect(() => {
    if (prefersReducedMotion()) { setValue(target); return }
    const start = performance.now()
    const origin = from.current
    let frame = 0
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - t, 4)
      const next = t < 1 ? origin + (target - origin) * eased : target
      from.current = next
      setValue(next)
      if (t < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [target, duration])
  const factor = 10 ** decimals
  return Math.round(value * factor) / factor
}

/** Nice round axis maximum and ticks for count data. */
export function niceScale(max: number, count = 4) {
  if (max <= 0) return { max: 1, ticks: [0, 1] }
  const raw = max / count
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)))
  const step = [1, 2, 2.5, 5, 10].map(m => m * magnitude).find(s => s >= raw) || magnitude * 10
  const stepInt = Math.max(1, Math.ceil(step))
  const top = Math.ceil(max / stepInt) * stepInt
  const ticks: number[] = []
  for (let v = 0; v <= top; v += stepInt) ticks.push(v)
  return { max: top, ticks }
}

/** Tracks an element's rendered width so SVG charts draw at true pixel size. */
export function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    setWidth(el.getBoundingClientRect().width)
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  return [ref, width] as const
}
